<template>
  <div class="i-mt-0 create-form">
    <settings-section-title v-if="isEditFlow" id="create" class="i-mb-4"
      >Edit Webhook</settings-section-title
    >
    <settings-section-title v-else id="create" class="i-mb-4"
      >Create Webhook</settings-section-title
    >
    <breadcrumbs class="i-mb-12" :items="breadcrumbs"></breadcrumbs>

    <info-status
      :text="infoMessage.text"
      :status="infoMessage.status"
    ></info-status>

    <div class="form d-flex flex-column i-gap-8">
      <i-text-input
        id="webhook-url"
        :value="tempWebhook.url"
        data-testid="webhook-url"
        label="Webhook URL"
        placeholder="https://www.example.com/webhook"
        :has-error="urlHasErrors"
        :show-hint-text="true"
        :hint-text="
          urlHasErrors
            ? 'The URL you provided is invalid. Webhooks must be https.'
            : isEditFlow && !testWebhookEnabled
              ? 'Save webhook changes to allow testing.'
              : null
        "
        @input="tempWebhook.url = $event"
      ></i-text-input>

      <div class="d-flex flex-row align-items-center">
        <i-select
          label="Event Type"
          data-testid="webhook-type"
          :options="displayOptions"
          :default-value="tempWebhook.type"
          placeholder="Please select an event"
          :class="{ error: hasAnyError }"
          @option-selected="
            (option) => {
              tempWebhook.type = option;
            }
          "
        ></i-select>

        <test-webhook
          v-if="isEditFlow"
          :webhook="tempWebhook"
          :disabled="!testWebhookEnabled"
          @flow-start="infoMessage.text = ''"
          @on-status="setStatus"
        ></test-webhook>
      </div>

      <div v-if="isDocumentProgressType" class="d-flex flex-column i-gap-4">
        <div class="d-flex align-items-center i-gap-2">
          <label class="i-text-body-2 i-text-neutral-120 i-m-0"
            >Progress Stages</label
          >
          <info-icon
            title="Progress Stages"
            description="
            Select which document processing stages should trigger this webhook. Choose 'Classified' for when documents are categorized, 'Parsed' for when data extraction is complete, and 'Done' for when all processing is finished"
          ></info-icon>
        </div>
        <i-checkbox
          v-for="stage in progressStages"
          :key="stage.value"
          v-model="tempWebhook.progressStages"
          :label="stage.label"
          :input-value="stage.value"
        />
        <span
          v-if="progressStagesHasErrors"
          class="i-text-body-3 i-text-error-90"
        >
          Please select at least one progress stage.
        </span>
      </div>

      <i-copy-input
        v-if="isEditFlow"
        :key="tempWebhook.secret"
        label="Secret Key"
        data-testid="webhook-secret"
        type="password"
        :value="tempWebhook.secret"
        @text-copied="onTextCopied"
      ></i-copy-input>

      <i-text-input
        id="webhook-url"
        :value="tempWebhook.description"
        label="Description (optional)"
        data-testid="webhook-description"
        :has-error="descriptionHasErrors"
        :show-hint-text="true"
        :hint-text="
          descriptionHasErrors ? 'Max length of 150 characters reached.' : null
        "
        :mode="iTextInputModes.TEXTAREA"
        @input="tempWebhook.description = $event"
      ></i-text-input>

      <div v-if="isEditFlow" class="d-flex flex-column">
        <i-toggle
          :toggle-value="tempWebhook.enabled"
          label="Status"
          :full-width="true"
          @value-update="(v) => (tempWebhook.enabled = v)"
        ></i-toggle>
      </div>
    </div>

    <div class="d-flex justify-flex-start align-items-center i-mt-11">
      <i-button
        v-if="!isEditFlow"
        data-testid="webhook-create"
        text="Create"
        size="lg"
        :loading="loading"
        :disabled="hasAnyError"
        @button-click="createKey()"
      ></i-button>

      <i-button
        v-if="isEditFlow"
        data-testid="webhook-save"
        text="Save"
        size="lg"
        :loading="loading"
        :disabled="hasAnyError"
        @button-click="editKey()"
      ></i-button>

      <i-button
        :text="isEditFlow ? 'Back' : 'Cancel'"
        data-testid="webhook-cancel"
        class="i-ml-6"
        type="neutral"
        subtype="subtle"
        size="lg"
        @button-click="goToList()"
      ></i-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import {
  iButton,
  iCheckbox,
  iCopyInput,
  iTextInputModes,
  iTextInput,
  iSelect,
  iToggle,
  showToast,
} from '@inscribe/signal';
import Breadcrumbs from '@/shared/components/Breadcrumbs';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';
import InfoStatus from '@/V2/settings/shared/InfoStatus';
import InfoIcon from '@/V2/components/InfoIcon';
import {
  deprecatedWebhookTypes,
  webhookTypes,
  documentReviewAgentWebhookTypes,
  businessComplianceAgentWebhookTypes,
  processingJobWebhookTypes,
} from '@/V2/settings/webhooks/WebhookTypes';
import { progressStages } from '@/V2/settings/webhooks/ProgressStages';
import TestWebhook from '@/V2/settings/webhooks/TestWebhook.vue';

export default {
  components: {
    Breadcrumbs,
    'test-webhook': TestWebhook,
    'info-status': InfoStatus,
    InfoIcon,
    iButton,
    iCheckbox,
    iTextInput,
    iCopyInput,
    iSelect,
    iToggle,
    'settings-section-title': SettingsSectionTitle,
  },
  props: {
    webhookId: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      breadcrumbs: [
        {
          text: 'Settings',
          to: { name: 'settings-view' },
        },
        {
          text: 'Webhooks',
          to: { name: 'webhooks-settings-list' },
        },
        {
          text: this.webhookId ? 'Edit Webhook' : 'Create Webhook',
          active: true,
        },
      ],
      infoMessage: {
        text: '',
        status: '',
      },
      iTextInputModes,
      infoMessageTimeout: undefined,
      isEditFlow: !!this.webhookId,
      tempWebhook: {
        id: this.webhookId || '',
        url: '',
        type: '',
        secret: '',
        description: '',
        enabled: true,
        progressStages: [],
      },
      routeWebhook: undefined,
      displayOptions: [],
      loading: false,
      progressStages,
    };
  },
  computed: {
    ...mapState(['webhooks']),
    ...mapGetters([
      'includeLegacyAgentOutputInApiResponse',
      'canViewBusinessComplianceAgent',
      'processingJobsEnabled',
    ]),
    urlHasErrors() {
      return (
        !this.tempWebhook?.url ||
        this.tempWebhook.url.length < 4 ||
        !this.tempWebhook.url.startsWith('https')
      );
    },
    typeHasErrors() {
      return !this.tempWebhook.type;
    },
    descriptionHasErrors() {
      return !this.tempWebhook.description?.length > 150;
    },
    progressStagesHasErrors() {
      return (
        this.isDocumentProgressType &&
        (!this.tempWebhook.progressStages ||
          this.tempWebhook.progressStages.length === 0)
      );
    },
    hasAnyError() {
      return (
        this.urlHasErrors ||
        this.typeHasErrors ||
        this.descriptionHasErrors ||
        this.progressStagesHasErrors
      );
    },
    testWebhookEnabled() {
      return (
        this.tempWebhook.type === this.routeWebhook?.type &&
        this.tempWebhook.url === this.routeWebhook?.url
      );
    },
    isDocumentProgressType() {
      return this.tempWebhook.type === 'document_progress';
    },
    options() {
      let allowedWebhookTypes = webhookTypes;
      if (this.includeLegacyAgentOutputInApiResponse) {
        allowedWebhookTypes = [
          ...allowedWebhookTypes,
          ...documentReviewAgentWebhookTypes,
        ];
      }
      if (this.canViewBusinessComplianceAgent) {
        allowedWebhookTypes = [
          ...allowedWebhookTypes,
          ...businessComplianceAgentWebhookTypes,
        ];
      }
      if (this.processingJobsEnabled) {
        allowedWebhookTypes = [
          ...allowedWebhookTypes,
          ...processingJobWebhookTypes,
        ];
      }
      return allowedWebhookTypes;
    },
  },
  mounted() {
    if (this.$route.query.newlyCreated) {
      this.setStatus('Webhook Created', 'success');
    }
    this.setupEditingWebhook();
    this.fetchWebhooks();
  },
  methods: {
    fetchWebhooks() {
      this.$store
        .dispatch('getWebhooks')
        .then(() => this.setupEditingWebhook());
    },
    setupEditingWebhook() {
      this.routeWebhook = this.$store.state.webhooks.find(
        ({ id }) => id === this.tempWebhook.id,
      );

      this.tempWebhook.url = this.routeWebhook?.url || '';
      this.tempWebhook.type = this.routeWebhook?.type || '';
      this.tempWebhook.secret = this.routeWebhook?.secret || '';
      this.tempWebhook.description = this.routeWebhook?.description || '';
      this.tempWebhook.enabled =
        typeof this.routeWebhook?.enabled === 'boolean'
          ? this.routeWebhook.enabled
          : this.tempWebhook.enabled;
      this.tempWebhook.progressStages =
        this.routeWebhook?.options?.stages || [];
      const editingWebhookIsDeprecated = deprecatedWebhookTypes.find(
        (o) => o.value === this.tempWebhook.type,
      );
      this.displayOptions = editingWebhookIsDeprecated
        ? this.options.concat(deprecatedWebhookTypes)
        : this.options;
    },
    setLoading(value) {
      this.loading = value;
    },
    async createKey() {
      this.setLoading(true);

      const payload = {
        url: this.tempWebhook.url,
        type: this.tempWebhook.type,
        options: {},
        description: this.tempWebhook.description,
        enabled: this.tempWebhook.enabled,
      };

      if (this.isDocumentProgressType) {
        payload.options = {
          stages: this.tempWebhook.progressStages,
        };
      }

      this.$store
        .dispatch('addWebhook', payload)
        .then((response) => {
          this.$store.commit('addWebhook', response?.data);
          this.setLoading(false);
          this.$router.push({
            name: 'webhooks-settings-edit',
            params: { webhookId: response?.data?.id },
            query: { newlyCreated: true },
          });
        })
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || 'Error creating the webhook';
          this.setStatus(errorMessage);
          showToast({
            text: errorMessage,
            isError: true,
          });
        })
        .finally(() => {
          this.setLoading(false);
        });
    },
    async editKey() {
      this.setLoading(true);

      const payload = {
        id: this.tempWebhook.id,
        url: this.tempWebhook.url,
        type: this.tempWebhook.type,
        options: {},
        description: this.tempWebhook.description,
        enabled: this.tempWebhook.enabled,
      };

      if (this.isDocumentProgressType) {
        payload.options = {
          stages: this.tempWebhook.progressStages,
        };
      }

      this.$store
        .dispatch('updateWebhook', payload)
        .then((response) => {
          this.$store.commit('updateGeneric', {
            objectsName: 'webhooks',
            updatedObject: {
              ...this.routeWebhook,
              ...response.data,
            },
          });
          this.setupEditingWebhook();
          this.setStatus('Webhook Saved', 'success');
          this.setLoading(false);
        })
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || 'Error modifying the webhook';
          this.setStatus(errorMessage);
          showToast({
            text: errorMessage,
            isError: true,
          });
        })
        .finally(() => {
          this.setLoading(false);
        });
    },
    setStatus(message, type = 'error') {
      this.setLoading(false);
      clearTimeout(this.infoMessageTimeout);
      this.infoMessage.status = type;
      this.infoMessage.text = message;
      this.infoMessageTimeout = setTimeout(() => {
        this.infoMessage.text = '';
      }, 8000);
    },
    goToList() {
      this.$router.push({ name: 'webhooks-settings-list' });
    },
    onTextCopied() {
      showToast({
        text: 'Webhook Secret Key copied',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.create-form {
  max-width: 500px;
}
</style>
